import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown, ArrowLeft, ArrowUp, GripVertical, ImagePlus, Loader2,
  Pencil, Plus, Star, Trash2, UploadCloud, X,
} from "lucide-react";
import { api, adminApi } from "@/lib/sleek";

const CATEGORIES = ["Kitchen", "Bath", "Whole-Home", "Millwork", "Tile"];

const emptyForm = { title: "", category: "Kitchen", tags: "", summary: "" };

export default function PortfolioManager({ token }) {
  const c = adminApi(token);
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(null); // project object → photo manager view
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/portfolio");
    setProjects(data);
    setSelected((prev) => (prev ? data.find((p) => p.id === prev.id) || null : null));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openEdit = (p) => {
    setEditingId(p.id);
    setShowCreate(false);
    setForm({ title: p.title, category: p.category, tags: (p.tags || []).join(", "), summary: p.summary || "" });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      summary: form.summary.trim(),
    };
    try {
      if (editingId) {
        await c.patch(`/admin/portfolio/${editingId}`, payload);
        toast.success("Project updated.");
      } else {
        const { data } = await c.post("/admin/portfolio", payload);
        toast.success(`"${data.title}" created — now add photos.`);
      }
      setShowCreate(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from the portfolio? Photos stay on disk but the project disappears from the site.`)) return;
    await c.delete(`/admin/portfolio/${p.id}`);
    toast.success("Project deleted.");
    load();
  };

  const moveProject = async (idx, dir) => {
    const next = [...projects];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setProjects(next);
    await c.put("/admin/portfolio/reorder", { ids: next.map((p) => p.id) });
  };

  if (selected) {
    return <PhotoManager token={token} project={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="mt-6 bg-white border border-zinc-200" data-testid="portfolio-manager">
      <div className="flex items-center justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Portfolio Manager</div>
          <div className="text-sm text-zinc-600 mt-1">{projects ? `${projects.length} projects` : "Loading…"} · reorder with arrows · click a project to manage photos</div>
        </div>
        <button
          onClick={() => { setShowCreate((v) => !v); setEditingId(null); setForm(emptyForm); }}
          className="flex items-center gap-2 bg-zinc-950 text-white text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-zinc-800 transition"
          data-testid="pm-new-project-btn"
        >
          <Plus size={14} /> New Project
        </button>
      </div>

      {(showCreate || editingId) && (
        <form onSubmit={submitForm} className="p-6 border-b border-zinc-100 bg-zinc-50 grid md:grid-cols-2 gap-4" data-testid="pm-project-form">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
              className="w-full border border-zinc-300 bg-white px-3 py-2 mt-1 focus:outline-none focus:border-zinc-950"
              placeholder="e.g. Newton Kitchen" data-testid="pm-form-title" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-zinc-300 bg-white px-3 py-2 mt-1" data-testid="pm-form-category">
              {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Tags (comma separated)</label>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full border border-zinc-300 bg-white px-3 py-2 mt-1 focus:outline-none focus:border-zinc-950"
              placeholder="Kitchen, Millwork" data-testid="pm-form-tags" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Summary</label>
            <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3}
              className="w-full border border-zinc-300 bg-white px-3 py-2 mt-1 focus:outline-none focus:border-zinc-950"
              placeholder="One or two sentences describing the project…" data-testid="pm-form-summary" />
          </div>
          <div className="flex gap-3 md:col-span-2">
            <button disabled={saving} className="bg-zinc-950 text-white text-xs tracking-[0.2em] uppercase px-6 py-2.5 hover:bg-zinc-800 transition disabled:opacity-50" data-testid="pm-form-submit">
              {saving ? "Saving…" : editingId ? "Save changes" : "Create project"}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setEditingId(null); }}
              className="text-xs tracking-[0.2em] uppercase px-4 py-2.5 border border-zinc-300 text-zinc-600 hover:border-zinc-950 hover:text-zinc-950 transition" data-testid="pm-form-cancel">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div>
        {(projects || []).map((p, idx) => (
          <div key={p.id} className="flex items-center gap-4 px-6 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition" data-testid={`pm-project-row-${p.id}`}>
            <div className="flex flex-col text-zinc-400">
              <button onClick={() => moveProject(idx, -1)} disabled={idx === 0} className="hover:text-zinc-950 disabled:opacity-20" data-testid={`pm-move-up-${p.id}`}><ArrowUp size={14} /></button>
              <button onClick={() => moveProject(idx, 1)} disabled={idx === projects.length - 1} className="hover:text-zinc-950 disabled:opacity-20" data-testid={`pm-move-down-${p.id}`}><ArrowDown size={14} /></button>
            </div>
            <button onClick={() => setSelected(p)} className="flex items-center gap-4 flex-1 min-w-0 text-left group" data-testid={`pm-open-project-${p.id}`}>
              {p.image ? (
                <img src={p.image} alt="" className="w-14 h-14 object-cover bg-zinc-100 shrink-0" />
              ) : (
                <div className="w-14 h-14 bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0"><ImagePlus size={18} /></div>
              )}
              <div className="min-w-0">
                <div className="font-medium truncate group-hover:underline">{p.title}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">{p.category} · {p.id}</div>
              </div>
            </button>
            <button onClick={() => setSelected(p)} className="text-xs tracking-[0.15em] uppercase text-zinc-600 hover:text-zinc-950 border border-zinc-300 hover:border-zinc-950 px-3 py-1.5 transition hidden sm:block" data-testid={`pm-manage-photos-${p.id}`}>
              Photos
            </button>
            <button onClick={() => openEdit(p)} className="text-zinc-500 hover:text-zinc-950 p-1.5" title="Edit details" data-testid={`pm-edit-${p.id}`}><Pencil size={15} /></button>
            <button onClick={() => deleteProject(p)} className="text-zinc-500 hover:text-red-600 p-1.5" title="Delete project" data-testid={`pm-delete-${p.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {projects && projects.length === 0 && <div className="p-10 text-center text-zinc-500">No projects yet — create your first one.</div>}
      </div>
    </div>
  );
}

function PhotoManager({ token, project, onBack }) {
  const c = adminApi(token);
  const [photos, setPhotos] = useState(null);
  const [hero, setHero] = useState("");
  const [order, setOrder] = useState([]); // local drag order
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(0); // remaining count
  const [dragIdx, setDragIdx] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await c.get(`/admin/portfolio/${project.id}/photos`);
    setPhotos(data.photos);
    setOrder(data.photos);
    setHero(data.hero);
    setDirty(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(files.length);
    for (const f of files) {
      const fd = new FormData();
      fd.append("files", f);
      try {
        await c.post(`/admin/portfolio/${project.id}/photos`, fd);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
      setUploading((n) => n - 1);
    }
    toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded.`);
    load();
  };

  const removePhoto = async (path) => {
    if (!window.confirm("Delete this photo permanently?")) return;
    const { data } = await c.delete(`/admin/portfolio/${project.id}/photos`, { data: { path } });
    setPhotos(data.photos);
    setOrder(data.photos);
    setHero(data.hero);
    setDirty(false);
    toast.success("Photo deleted.");
  };

  const setAsHero = async (path) => {
    await c.patch(`/admin/portfolio/${project.id}`, { image: path });
    setHero(path);
    toast.success("Hero image updated.");
  };

  const saveOrder = async () => {
    const { data } = await c.put(`/admin/portfolio/${project.id}/photos/reorder`, { paths: order });
    setPhotos(data.photos);
    setOrder(data.photos);
    setHero(data.hero);
    setDirty(false);
    toast.success("Photo order saved.");
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDropTarget(null); return; }
    const next = [...order];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setOrder(next);
    setDirty(true);
    setDragIdx(null);
    setDropTarget(null);
  };

  return (
    <div className="mt-6 bg-white border border-zinc-200" data-testid="pm-photo-manager">
      <div className="flex items-center justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-zinc-950" data-testid="pm-back-btn">
            <ArrowLeft size={14} /> All projects
          </button>
          <div>
            <div className="font-display text-xl">{project.title}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{photos ? `${photos.length} photos` : "Loading…"} · drag tiles to reorder · star = hero image</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {dirty && (
            <button onClick={saveOrder} className="bg-emerald-600 text-white text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-emerald-700 transition" data-testid="pm-save-order-btn">
              Save order
            </button>
          )}
          <button onClick={() => fileRef.current?.click()} disabled={uploading > 0}
            className="flex items-center gap-2 bg-zinc-950 text-white text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-zinc-800 transition disabled:opacity-50" data-testid="pm-upload-btn">
            {uploading > 0 ? <><Loader2 size={14} className="animate-spin" /> Uploading ({uploading} left)</> : <><UploadCloud size={14} /> Upload photos</>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="pm-file-input"
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      <div
        className="p-6"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); }}
      >
        {photos && photos.length === 0 && (
          <button onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-zinc-300 py-16 text-zinc-500 hover:border-zinc-950 hover:text-zinc-950 transition flex flex-col items-center gap-3" data-testid="pm-empty-dropzone">
            <UploadCloud size={28} />
            <div className="text-sm">Drop photos here or click to upload — first photo becomes the hero.</div>
          </button>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {order.map((p, idx) => (
            <div
              key={p}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropTarget(idx); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(idx); }}
              onDragEnd={() => { setDragIdx(null); setDropTarget(null); }}
              className={`relative group cursor-grab active:cursor-grabbing border ${dropTarget === idx && dragIdx !== null && dragIdx !== idx ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200"} ${dragIdx === idx ? "opacity-40" : ""}`}
              data-testid={`pm-photo-tile-${idx}`}
            >
              <img src={p} alt="" className="w-full aspect-[4/3] object-cover bg-zinc-100 pointer-events-none" />
              <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
                <span className="bg-zinc-950/80 text-white text-[10px] font-mono px-1.5 py-0.5">{idx + 1}</span>
                {hero === p && (
                  <span className="bg-amber-400 text-zinc-950 text-[9px] tracking-[0.15em] uppercase font-medium px-1.5 py-0.5" data-testid={`pm-hero-badge-${idx}`}>Hero</span>
                )}
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/80 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical size={14} className="text-white/70" />
                <div className="flex items-center gap-1">
                  {hero !== p && (
                    <button onClick={() => setAsHero(p)} title="Set as hero image" className="text-white/80 hover:text-amber-400 p-1" data-testid={`pm-set-hero-${idx}`}>
                      <Star size={15} />
                    </button>
                  )}
                  <button onClick={() => removePhoto(p)} title="Delete photo" className="text-white/80 hover:text-red-400 p-1" data-testid={`pm-delete-photo-${idx}`}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
